export type LeaveStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'RETURNED' | 'CANCELLED'
export type DayPart = 'FULL_DAY' | 'FIRST_HALF' | 'SECOND_HALF'

export interface LeaveTypeView {
  id: string
  code: string
  name: string
  paid: boolean
  requiresDocument: boolean
  color: string | null
}

export interface BalanceView {
  leaveTypeId: string
  leaveTypeName: string
  leaveTypeCode: string
  year: number
  entitled: number
  used: number
  pending: number
  available: number
  /**
   * The type keeps no balance, so `entitled` and `available` are arithmetic rather than facts.
   *
   * <p>`available` goes negative as unpaid leave is taken — it is entitlement minus taken, and
   * the entitlement is zero because there is none. A card reading "−3 of 0" reports a limit
   * that does not exist, so an unbounded type shows what was taken and says there is no limit.
   */
  unbounded: boolean
}

export type RejoinStatus = 'NOT_REQUIRED' | 'AWAITING' | 'CONFIRMED'
export type RejoinOutcome = 'ON_TIME' | 'EARLY' | 'LATE'
export type LateTreatment = 'EXTENSION_APPROVED' | 'UNPAID' | 'ABSENT'
export type DayUnit = 'CALENDAR_DAYS' | 'WORKING_DAYS'
export type AccrualBasis = 'NONE' | 'MONTHLY' | 'ANNUAL_GRANT'

/**
 * What happens to the days between the planned return and the actual one.
 *
 * A policy decision rather than a calculation, which is why the person confirming has to
 * choose it — burying it in code would make it one nobody could see or change.
 */
export const LATE_TREATMENT_LABELS: Record<LateTreatment, string> = {
  EXTENSION_APPROVED: 'Covered by an approved extension',
  UNPAID: 'Unpaid — not leave, not held against them',
  ABSENT: 'Recorded as absence',
}

export interface RequestView {
  id: string
  employeeId: string
  employeeName: string | null
  leaveTypeId: string
  leaveTypeName: string
  fromDate: string
  toDate: string
  dayPart: DayPart
  days: number
  reason: string | null
  status: LeaveStatus
  workflowInstanceId: string | null
  appliedAt: string | null
  decidedAt: string | null
  decisionComment: string | null
  /** Null until a return is confirmed; screens fall back to fromDate/toDate. */
  confirmedFromDate: string | null
  confirmedToDate: string | null
  rejoinStatus: RejoinStatus
  parentLeaveRequestId: string | null
}

/** One band of a tiered entitlement — UAE sick leave is 15 full, 30 half, 45 unpaid. */
export interface TierView {
  id: string | null
  seq: number
  fromDay: number
  toDay: number | null
  payPercent: number
  label: string | null
}

export interface LeaveTypeDetailView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  paid: boolean
  requiresDocument: boolean
  maxConsecutiveDays: number | null
  genderRestriction: string | null
  countsNonWorkingDays: boolean
  color: string | null
  active: boolean
  /** Null for a type that is company policy rather than law — Hajj leave, for instance. */
  statutoryCode: string | null
  statutoryReference: string | null
  seeded: boolean
  seedVersion: number | null
  dayUnit: DayUnit
  annualEntitlementDays: number | null
  maxDaysPerYear: number | null
  accrualBasis: AccrualBasis
  /**
   * No balance is kept for this type.
   *
   * <p>The difference between a limit of none and a limit of zero. Unpaid leave has no accrual
   * and no annual figure, and that used to be read as an entitlement of zero — so every
   * request for it was refused for insufficient balance.
   */
  unbounded: boolean
  minServiceMonths: number | null
  fullEntitlementAfterMonths: number | null
  partialAccrualDaysPerMonth: number | null
  carryForwardMaxDays: number | null
  encashable: boolean
  requiresRejoinConfirmation: boolean
  airTicketEligible: boolean
  tiers: TierView[]
}

export interface RejoinView {
  id: string
  leaveRequestId: string
  employeeId: string
  plannedReturnDate: string
  actualReturnDate: string
  varianceDays: number
  outcome: RejoinOutcome
  lateTreatment: LateTreatment | null
  extensionRequestId: string | null
  balanceAdjustment: number
  airTicketClaimId: string | null
  remarks: string | null
  confirmedAt: string
  confirmedFromDate: string | null
  confirmedToDate: string | null
}

export interface SeedPackResult {
  legalEntityId: string
  typesCreated: number
  message: string
}

/** One colleague away over the same days, and how many of them overlap. */
export interface TeamOverlapView {
  employeeId: string
  employeeName: string
  fromDate: string
  toDate: string
  status: string
  overlappingDays: number
}

/**
 * Everything the leave approval screen needs in one call.
 *
 * `balanceForThisType` is null for an unpaid or non-accruing type, or a first request in a
 * new year — which is different from a balance of zero and must read differently.
 */
export interface LeaveDecisionContext {
  request: RequestView
  balanceForThisType: BalanceView | null
  allBalances: BalanceView[]
  recentRequests: RequestView[]
  teamOverlap: TeamOverlapView[]
  documentFileId: string | null
}

